// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
/**
 * Registry: kiest de best beschikbare telefooncontact-provider voor de huidige runtime
 * en levert ontdubbel-hulp. De UI roept uitsluitend deze functies aan en blijft zo los
 * van de concrete bron.
 *
 * Voorkeursvolgorde: native (Fase 2) → Web Contact Picker (Android nu). Op iOS Safari en
 * desktop is er geen bron → getPhoneContactProvider() geeft null en de UI toont de
 * nette terugvalmelding.
 */
import type { ContactProvider, ProvidedContact } from './types'
import { nativeContactProvider } from './providers/nativeProvider'
import { webPickerProvider } from './providers/webPickerProvider'

const PHONE_PROVIDERS: ContactProvider[] = [nativeContactProvider, webPickerProvider]

/** De best beschikbare telefooncontact-provider, of null als geen enkele bron werkt. */
export function getPhoneContactProvider(): ContactProvider | null {
  return PHONE_PROVIDERS.find(p => p.isSupported()) ?? null
}

export function isPhoneContactsSupported(): boolean {
  return getPhoneContactProvider() !== null
}

const normPhone = (p?: string) => (p ?? '').replace(/\D/g, '')
const normEmail = (e?: string) => (e ?? '').trim().toLowerCase()
const normName  = (n?: string) => (n ?? '').trim().toLowerCase()

/**
 * Ontdubbel binnenkomende contacten tegen reeds bekende contacten (partner, kind,
 * vertrouwenspersoon, executeur, eerder geïmporteerd, …). Match op telefoonnummer,
 * anders e-mail, anders naam. Voorkomt dubbele contacten conform de eis.
 */
export function dedupeContacts<T extends { phone?: string; email?: string; name?: string }>(
  incoming: ProvidedContact[],
  existing: T[],
): ProvidedContact[] {
  const phones = new Set(existing.map(e => normPhone(e.phone)).filter(Boolean))
  const emails = new Set(existing.map(e => normEmail(e.email)).filter(Boolean))
  const names  = new Set(existing.map(e => normName(e.name)).filter(Boolean))

  const seenPhones = new Set<string>()
  const seenEmails = new Set<string>()
  const out: ProvidedContact[] = []

  for (const c of incoming) {
    const ph = normPhone(c.phone)
    const em = normEmail(c.email)
    const nm = normName(c.name)

    if (ph) {
      if (phones.has(ph) || seenPhones.has(ph)) continue
      seenPhones.add(ph)
    } else if (em) {
      if (emails.has(em) || seenEmails.has(em)) continue
      seenEmails.add(em)
    } else if (nm && names.has(nm)) {
      continue
    }
    out.push(c)
  }
  return out
}
